import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Modal,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";

import {
  useGetEmailConfigQuery,
  useEmailConfigMutation,
  useUpdateEmailConfigMutation,
  // useDeleteEmailConfigMutation,
} from "../../../Services/inventory/Inventory";

const SECURITY_TYPES = ["SSL", "TLS"];

const ORANGE = "#ff9800";
const ORANGE_LIGHT = "#fff3e0";

const EmailConfig = () => {
  const {
    data: emailConfigResponse,
    isLoading,
    isError,
    refetch,
  } = useGetEmailConfigQuery({});
  const [createEmailConfig] = useEmailConfigMutation();
  const [updateEmailConfig] = useUpdateEmailConfigMutation();
  // const [deleteEmailConfig] = useDeleteEmailConfigMutation();

  const [data, setData] = useState<any[]>([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    id: 0,
    smtp_host: "",
    smtp_port: "",
    smtp_username: "",
    smtp_password: "",
    smtp_from_email: "",
    smtp_security_type: "",
  });
  const [originalFormData, setOriginalFormData] = useState({
    id: 0,
    smtp_host: "",
    smtp_port: "",
    smtp_username: "",
    smtp_password: "",
    smtp_from_email: "",
    smtp_security_type: "",
  });
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    if (emailConfigResponse?.data) {
      if (Array.isArray(emailConfigResponse.data)) {
        setData(emailConfigResponse.data);
      } else {
        setData([emailConfigResponse.data]);
      }
    } else {
      setData([]);
    }
  }, [emailConfigResponse]);

  const handleOpenModal = (config?: any) => {
    if (config) {
      // Convert the security type to uppercase to match the SECURITY_TYPES array
      const formattedConfig = {
        ...config,
        smtp_security_type: config.smtp_security_type
          ? config.smtp_security_type.toUpperCase()
          : "",
      };
      setFormData(formattedConfig);
      setOriginalFormData(formattedConfig); // Store original data for comparison
    } else {
      const newConfig = {
        id: 0,
        smtp_host: "",
        smtp_port: "",
        smtp_username: "",
        smtp_password: "",
        smtp_from_email: "",
        smtp_security_type: "",
      };
      setFormData(newConfig);
      setOriginalFormData(newConfig); // Store original data for comparison
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => setIsModalOpen(false);

  const handleOpenDelete = (id: number) => {
    setSelectedId(id);
    setIsDeleteOpen(true);
  };

  const handleCloseDelete = () => {
    setSelectedId(null);
    setIsDeleteOpen(false);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: any) => {
    setFormData((prev) => ({
      ...prev,
      smtp_security_type: e.target.value as string,
    }));
  };

  // Check if form has been modified
  const hasFormChanged = () => {
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  };

  // Validate all required fields are non-empty/non-zero
  const isFormValid = () => {
    const {
      smtp_host,
      smtp_port,
      smtp_username,
      smtp_password,
      smtp_from_email,
      smtp_security_type,
    } = formData;

    return (
      smtp_host.trim() !== "" &&
      smtp_port !== "" &&
      Number(smtp_port) > 0 &&
      smtp_username.trim() !== "" &&
      smtp_password.trim() !== "" &&
      smtp_from_email.trim() !== "" &&
      smtp_security_type.trim() !== ""
    );
  };

  // Check if save should be enabled (form is valid AND has changes OR it's a new config)
  const isSaveEnabled = () => {
    const isValid = isFormValid();
    const isNewConfig = !formData.id || formData.id === 0;
    const hasChanges = hasFormChanged();

    return isValid && (isNewConfig || hasChanges);
  };

  // Check if Add button should be hidden
  const isAddButtonDisabled = () => {
    return isLoading || data.length > 0; // Hide if loading or data exists
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      if (formData.id && formData.id !== 0) {
        // Update existing config - separate ID and body
        const { id, ...bodyData } = formData;

        // Convert security type back to lowercase before sending to API
        const apiData = {
          ...bodyData,
          smtp_security_type: bodyData.smtp_security_type.toLowerCase(),
        };

        await updateEmailConfig({
          id: id,
          body: apiData,
        }).unwrap();
        toast.success("Email configuration updated successfully");
      } else {
        // Create new config - remove id from formData for creation
        const { id, ...createData } = formData;

        // Convert security type back to lowercase before sending to API
        const apiData = {
          ...createData,
          smtp_security_type: createData.smtp_security_type.toLowerCase(),
        };

        await createEmailConfig(apiData).unwrap();
        toast.success("Email configuration created successfully");
      }
      await refetch();
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error saving Email config", error);
      let message = "Failed to save Email configuration";
      if (error?.data?.error) {
        message = error.data.error;
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // const handleDelete = async () => {
  //   if (selectedId == null) return;
  //   setLoading(true);
  //   try {
  //     await deleteEmailConfig({ id: selectedId }).unwrap();
  //     await refetch();
  //     setIsDeleteOpen(false);
  //     toast.success("Email configuration deleted successfully");
  //   } catch (error) {
  //     console.error("Error deleting Email config", error);
  //     toast.error("Failed to delete Email configuration");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography
          variant="h6"
          fontWeight={200}
          color="text.primary"
          style={{ fontFamily: "karbon", color: "#666666" }}
        >
          Email Configuration
        </Typography>
        {!isAddButtonDisabled() && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
            style={{ fontFamily: "karbon" }}
            sx={{
              fontSize: 12,
              backgroundColor: ORANGE,
              textTransform: "none",
              fontWeight: 200,
              px: 3,
              py: 1.5,
              borderRadius: 2,
              "&:hover": {
                backgroundColor: "#e65100",
              },
            }}
          >
            Add Email Config
          </Button>
        )}
      </Box>

      {isError ? (
        <Typography color="error">Failed to load Email config</Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead
              style={{
                backgroundColor: ORANGE_LIGHT,
                borderBottom: `2px solid ${ORANGE}`,
                fontFamily: "karbon",
              }}
            >
              <tr>
                <th
                  style={{
                    padding: "12px",
                    fontWeight: 100,
                    textAlign: "left",
                    color: ORANGE,
                  }}
                >
                  SMTP Host
                </th>
                <th
                  style={{
                    padding: "12px",
                    fontWeight: 600,
                    textAlign: "left",
                    color: ORANGE,
                  }}
                >
                  SMTP Port
                </th>
                <th
                  style={{
                    padding: "12px",
                    fontWeight: 600,
                    textAlign: "left",
                    color: ORANGE,
                  }}
                >
                  SMTP Username
                </th>
                <th
                  style={{
                    padding: "12px",
                    fontWeight: 600,
                    textAlign: "left",
                    color: ORANGE,
                  }}
                >
                  SMTP From Email
                </th>
                <th
                  style={{
                    padding: "12px",
                    fontWeight: 600,
                    textAlign: "left",
                    color: ORANGE,
                  }}
                >
                  SMTP Security
                </th>
                <th
                  style={{
                    padding: "12px",
                    fontWeight: 600,
                    textAlign: "left",
                    color: ORANGE,
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    style={{
                      padding: "20px",
                      textAlign: "center",
                      color: "#777",
                    }}
                  >
                    No configurations found. Add your first Email configuration.
                  </td>
                </tr>
              ) : (
                data.map((row) => (
                  <tr
                    key={row.id}
                    style={{
                      cursor: "default",
                      fontFamily: "karbon",
                      color: "#666666",
                      fontWeight: 100,
                    }}
                  >
                    <td style={{ padding: "10px" }}>{row.smtp_host}</td>
                    <td style={{ padding: "10px" }}>{row.smtp_port}</td>
                    <td style={{ padding: "10px" }}>{row.smtp_username}</td>
                    <td style={{ padding: "10px" }}>{row.smtp_from_email}</td>
                    <td style={{ padding: "10px" }}>
                      {row.smtp_security_type.toUpperCase()}
                    </td>

                    <td style={{ padding: "10px" }}>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            sx={{ color: ORANGE }}
                            onClick={() => handleOpenModal(row)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {/* <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDelete(row.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip> */}
                      </Box>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Box>
      )}

      {/* Add/Edit Modal */}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        disableEscapeKeyDown={loading}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            maxHeight: "90vh",
            overflowY: "auto",
            width: 600,
            p: 3,
          }}
        >
          <Typography variant="h6" mb={2}>
            {formData.id && formData.id !== 0
              ? "Edit Email Configuration"
              : "Add Email Configuration"}
          </Typography>

          <Grid container spacing={2}>
            {/* SMTP Host */}
            <Grid item xs={12}>
              <Typography
                variant="body2"
                fontWeight={100}
                mb={1}
                className="required"
                style={{ fontFamily: "karbon", color: "#666666" }}
              >
                SMTP Host
              </Typography>
              <TextField
                name="smtp_host"
                value={formData.smtp_host}
                onChange={handleInputChange}
                fullWidth
                disabled={loading}
                required
                placeholder="Enter SMTP host"
                style={{ fontFamily: "karbon", color: "#666666" }}
              />
            </Grid>

            {/* SMTP Port */}
            <Grid item xs={6}>
              <Typography
                variant="body2"
                fontWeight={100}
                mb={1}
                className="required"
                style={{ fontFamily: "karbon", color: "#666666" }}
              >
                SMTP Port
              </Typography>
              <TextField
                name="smtp_port"
                type="number"
                value={formData.smtp_port}
                onChange={handleInputChange}
                fullWidth
                disabled={loading}
                required
                inputProps={{ min: 1 }}
                placeholder="Enter SMTP port"
                style={{ fontFamily: "karbon", color: "#666666" }}
              />
            </Grid>

            {/* SMTP Security Type */}
            <Grid item xs={6}>
              <Typography
                variant="body2"
                fontWeight={100}
                mb={1}
                className="required"
                style={{ fontFamily: "karbon", color: "#666666" }}
              >
                SMTP Security Type
              </Typography>
              <FormControl
                fullWidth
                required
                disabled={loading}
                style={{ fontFamily: "karbon", color: "#666666" }}
              >
                <Select
                  value={formData.smtp_security_type}
                  onChange={handleSelectChange}
                  displayEmpty
                  style={{
                    fontFamily: "karbon",
                    color: "#666666",
                    fontWeight: 100,
                  }}
                >
                  <MenuItem
                    value=""
                    disabled
                    style={{
                      fontFamily: "karbon",
                      color: "#666666",
                      fontWeight: 100,
                    }}
                  >
                    Select
                  </MenuItem>
                  {SECURITY_TYPES.map((stype) => (
                    <MenuItem
                      value={stype}
                      key={stype}
                      style={{
                        fontFamily: "karbon",
                        color: "#666666",
                        fontWeight: 100,
                      }}
                    >
                      {stype}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* SMTP Username */}
            <Grid item xs={12}>
              <Typography
                variant="body2"
                fontWeight={100}
                mb={1}
                className="required"
                style={{ fontFamily: "karbon", color: "#666666" }}
              >
                SMTP Username
              </Typography>
              <TextField
                name="smtp_username"
                value={formData.smtp_username}
                onChange={handleInputChange}
                fullWidth
                disabled={loading}
                required
                placeholder="Enter SMTP username"
                style={{ fontFamily: "karbon", color: "#666666" }}
              />
            </Grid>

            {/* SMTP Password */}
            <Grid item xs={12}>
              <Typography
                variant="body2"
                fontWeight={100}
                mb={1}
                className="required"
                style={{ fontFamily: "karbon", color: "#666666" }}
              >
                SMTP Password
              </Typography>
              <TextField
                name="smtp_password"
                type="password"
                value={formData.smtp_password}
                onChange={handleInputChange}
                fullWidth
                disabled={loading}
                required
                placeholder="Enter SMTP password"
                style={{ fontFamily: "karbon", color: "#666666" }}
              />
            </Grid>

            {/* SMTP From Email */}
            <Grid item xs={12}>
              <Typography
                variant="body2"
                fontWeight={100}
                mb={1}
                className="required"
                style={{ fontFamily: "karbon", color: "#666666" }}
              >
                SMTP From Email
              </Typography>
              <TextField
                name="smtp_from_email"
                value={formData.smtp_from_email}
                onChange={handleInputChange}
                fullWidth
                disabled={loading}
                required
                type="email"
                placeholder="Enter from email address"
                style={{ fontFamily: "karbon", color: "#666666" }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              onClick={handleCloseModal}
              disabled={loading}
              sx={{
                textTransform: "none",
                px: 3,
                color: ORANGE,
                borderColor: ORANGE,
                "&:hover": {
                  backgroundColor: ORANGE_LIGHT,
                  borderColor: ORANGE,
                },
              }}
              style={{ fontFamily: "karbon" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading || !isSaveEnabled()}
              sx={{
                textTransform: "none",
                px: 3,
                backgroundColor: ORANGE,
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#e65100",
                },
              }}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default EmailConfig;
